import { useCallback, useEffect, useState } from 'react';
import type { AdminEmailTemplateRow, AdminEmailTemplateUpdate } from '../../../domain/superadmin/types';
import { useAppContext } from '../../../contexts/AppContext';
import {
  fetchAdminEmailTemplates,
  updateAdminEmailTemplate,
} from '../../../services/modules/superadmin/superadminApi';
import { SuperadminApiError } from '../../../services/modules/superadmin/errors';
import { notify } from '../../../utils/toast';

export function useAdminEmailTemplates() {
  const [items, setItems] = useState<AdminEmailTemplateRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { setCurrentPage } = useAppContext();

  const handleForbidden = useCallback(() => {
    setCurrentPage('dashboard');
    notify.warning('Acesso não autorizado à área de superadmin.');
  }, [setCurrentPage]);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await fetchAdminEmailTemplates();
      setItems(result.items);
    } catch (e) {
      if (e instanceof SuperadminApiError && e.isForbidden) {
        handleForbidden();
        return;
      }
      setError(e instanceof Error ? e.message : 'Erro ao carregar templates de e-mail');
    } finally {
      setIsLoading(false);
    }
  }, [handleForbidden]);

  const saveTemplate = useCallback(
    async (key: string, payload: AdminEmailTemplateUpdate) => {
      setIsSaving(true);
      setError(null);
      try {
        await updateAdminEmailTemplate(key, payload);
        const result = await fetchAdminEmailTemplates();
        setItems(result.items);
      } catch (e) {
        if (e instanceof SuperadminApiError && e.isForbidden) {
          handleForbidden();
          return;
        }
        setError(e instanceof Error ? e.message : 'Erro ao salvar template de e-mail');
      } finally {
        setIsSaving(false);
      }
    },
    [handleForbidden]
  );

  useEffect(() => {
    void load();
  }, [load]);

  return { items, isLoading, error, refetch: load, saveTemplate, isSaving };
}
